import React, { useEffect, useState } from "react";
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Breadcrumbs,
  Link as MuiLink,
  Tooltip,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { useLocation, Link, useNavigate } from "react-router-dom";

import { PROFILE } from "../constant/MenuItem";
import { User_Avatar } from "../assets/images";
import { getSubscriptionDetailsByUserId } from "../services/api/subscription";
import { showToast } from "../store/slices/toastSlice";

import {
  StyledListItemButton,
  StyledListItemIcon,
  StyledListItemText,
} from "../styles/SidenavStyles";

const Header = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path) => location.pathname.startsWith(path);
  const pathnames = location.pathname.split("/").filter((x) => x);

  const [anchorElUser, setAnchorElUser] = useState(null);
  const [planDetails, setPlanDetails] = useState([]);
  const [daysLeft, setDaysLeft] = useState(null);

  useEffect(() => {
    const fetchSubscriptionDetails = async () => {
      try {
        const response = await getSubscriptionDetailsByUserId();
        if (response.responseCode === 200) {
          setPlanDetails(response.data);

          // Calculate the number of days left
          const endDate = new Date(response.data.end_date); // Assuming `end_date` is in a valid format
          const currentDate = new Date();
          const timeDifference = endDate - currentDate;

          // Calculate the number of days remaining
          const daysRemaining = Math.ceil(timeDifference / (1000 * 3600 * 24)); // Convert from milliseconds to days
          setDaysLeft(daysRemaining);
        }
      } catch (error) {
        dispatch(showToast({ message: error.message, severity: "error" }));
      }
    };

    fetchSubscriptionDetails();
  }, []);

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleNavigation = (redirectTo) => {
    navigate(redirectTo);
    setAnchorElUser(null);
  };

  // Determine the color based on days left
  const getColor = (days) => {
    if (days < 15) return "red";
    if (days >= 15 && days < 30) return "grey";
    return "green"; // days >= 30
  };

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        backgroundColor: "#FFFFFF",
        // Soft border matching the premium financial UI layout in website_adv_banner.jpg
        borderBottom: "1px solid #E4EBF4",
      }}
    >
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#FFFFFF",
          height: "64px", // Matches the exact height of the Sidenav brand bar layer
          px: { xs: 2, md: 3 },
        }}
      >
        {/* BREADCRUMBS (Left Alignment Panel) */}
        <Breadcrumbs
          aria-label="breadcrumb"
          sx={{
            "& .MuiBreadcrumbs-separator": {
              color: "#8A9BB4",
              mx: 1,
            }
          }}
        >
          <MuiLink
            component={Link}
            underline="hover"
            sx={{
              color: "#8A9BB4",
              fontSize: "14px",
              fontWeight: "500",
              textDecoration: "none",
              "&:hover": { color: "#1877F2" }
            }}
            to="/dashboard"
          >
            Dashboard
          </MuiLink>

          {pathnames.map((value, index) => {
            const to = `/${pathnames.slice(0, index + 1).join("/")}`;
            const isLast = index === pathnames.length - 1;

            return isLast ? (
              <Typography
                key={to}
                sx={{
                  color: "#111B29", // High contrast active text
                  fontSize: "14px",
                  fontWeight: "600"
                }}
              >
                {formatPathSegment(value)}
              </Typography>
            ) : (
              <MuiLink
                component={Link}
                underline="hover"
                key={to}
                to={to}
                sx={{
                  color: "#8A9BB4",
                  fontSize: "14px",
                  fontWeight: "500",
                  textDecoration: "none",
                  "&:hover": { color: "#1877F2" }
                }}
              >
                {formatPathSegment(value)}
              </MuiLink>
            );
          })}
        </Breadcrumbs>

        {/* Subscription details and user profile */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {/* Subscription details */}
          {daysLeft !== null && (
            <Tooltip
              title={`Subscription ends on: ${new Date(
                planDetails.end_date
              ).toLocaleDateString("en-GB")}`}
              arrow
            >
              <Typography
                variant="h6"
                sx={{
                  mr: 3,
                  border: 1,
                  borderRadius: 1,
                  pl: 2,
                  pr: 2,
                  color: getColor(daysLeft), // Apply color based on daysLeft
                }}
              >
                {daysLeft} days left
              </Typography>
            </Tooltip>
          )}
        </Box>

        {/* PROFILE CONTROL CENTER (Right Alignment Panel) */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Tooltip title="Account settings">
            <IconButton
              onClick={handleOpenUserMenu}
              sx={{
                p: 0.5,
                border: "2px solid #E4EBF4",
                transition: "border-color 0.2s",
                "&:hover": { borderColor: "#1877F2" }
              }}
            >
              <Avatar
                alt="Profile Avatar"
                src={User_Avatar}
                sx={{ width: 36, height: 36 }}
              />
            </IconButton>
          </Tooltip>

          {/* CLEAN CONTEXTUAL DROPDOWN MENU */}
          <Menu
            id="menu-appbar"
            anchorEl={anchorElUser}
            open={Boolean(anchorElUser)}
            onClose={handleCloseUserMenu}
            disableScrollLock // Prevents the layout body from shifting layout footprints when toggling
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              elevation: 0,
              sx: {
                mt: 1.5,
                minWidth: 180,
                borderRadius: "8px",
                border: "1px solid #E4EBF4",
                boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
                p: 0.5,
              },
            }}
          >
            {PROFILE.map((item, index) => {
              const active = isActive(item.redirectTo);
              const IconComponent = active ? item.iconFilled : item.iconOutlined;

              return (
                <MenuItem
                  key={index}
                  onClick={() => handleNavigation(item.redirectTo)}
                  sx={{
                    borderRadius: "6px",
                    py: 1,
                    px: 1.5,
                    my: 0.2,
                    color: active ? "#1877F2" : "#111B29",
                    backgroundColor: active ? "rgba(24, 119, 242, 0.06)" : "transparent",
                    "&:hover": {
                      backgroundColor: active ? "rgba(24, 119, 242, 0.1)" : "#F4F7FC",
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: "32px !important",
                      color: active ? "#1877F2" : "#8A9BB4"
                    }}
                  >
                    <IconComponent sx={{ fontSize: "20px" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.title}
                    primaryTypographyProps={{
                      fontSize: "14px",
                      fontWeight: active ? "600" : "500"
                    }}
                  />
                </MenuItem>
              );
            })}
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

// Utility: Capitalize and format paths
const formatPathSegment = (segment) =>
  segment.replace(/-/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

export default Header;